/**
 * Icon set scoped to PlaylistRow (tag pills, meta row, status column).
 *
 * All icons inherit color via `currentColor` so the parent pill controls hue.
 * Sizing is uniform 16x16 viewBox; pass `size` to scale.
 */
import { FC } from "react";

interface IconProps {
  size?: number | string;
  className?: string;
}

const strokeProps = (size: number | string, className?: string) => ({
  width: size,
  height: size,
  viewBox: "0 0 16 16",
  xmlns: "http://www.w3.org/2000/svg",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  className,
});

const fillProps = (size: number | string, className?: string) => ({
  width: size,
  height: size,
  viewBox: "0 0 16 16",
  xmlns: "http://www.w3.org/2000/svg",
  fill: "currentColor",
  className,
});

/* Tag-pill icons (FlameSolid / Skull / Scope / ArrowUp / Crosshair / Bullet
   / Lightning) used to live here for the old in-house TagPill. They moved
   to MVVM/Components/ClipBadge + react-icons when the branded playlist
   adopted the canonical match-history badge set. Kept the file for the
   meta-row + status icons that still render in PlaylistRow itself. */

/** Up-trending chart for view counts. */
export const ChartIcon: FC<IconProps> = ({ size = 12, className }) => (
  <svg {...strokeProps(size, className)}>
    <path d="M2 12 6 8l2.5 2.5L13 5.5" />
    <path d="M9.5 5.5H13V9" />
  </svg>
);

/** Disc / CD icon — event logo placeholder in the meta row (Figma image_3). */
export const DiscIcon: FC<IconProps> = ({ size = 12, className }) => (
  <svg {...strokeProps(size, className)}>
    <circle cx="8" cy="8" r="6.4" />
    <circle cx="8" cy="8" r="2.6" />
    <circle cx="8" cy="8" r="0.9" fill="currentColor" stroke="none" />
  </svg>
);

/** Solid play triangle for status badges + circular play buttons. */
export const PlayIcon: FC<IconProps> = ({ size = 10, className }) => (
  <svg {...fillProps(size, className)}>
    <path d="M4 2.5v11l9-5.5z" />
  </svg>
);

/** "VS" between two team logos — small text-style separator. */
export const VsBadge: FC<IconProps> = ({ size = 14, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 14"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <text
      x="10"
      y="11"
      textAnchor="middle"
      fontFamily="Inter, system-ui, -apple-system, sans-serif"
      fontWeight="800"
      fontSize="10"
      fill="currentColor"
      letterSpacing="0.4"
    >
      VS
    </text>
  </svg>
);
